import { Dispatch, SetStateAction, useRef, useState } from 'react';

import { CONDITIONS_TAB_TEST_ID } from '../../Applicant.types';
import { useTabNavigation } from '../../hooks/useTabNavigation';
import { Condition } from './ApplicantConditions.types';
import { useConditionSuccessBanner } from './ConditionSuccessBanner';
import { FocusIntent } from './useConditionFocus';

interface AttachedBanner {
    condition: Condition;
    count: number;
}

interface UseConditionAttachDialogOptions {
    showBanner: ReturnType<typeof useConditionSuccessBanner>['showBanner'];
    setFocusIntent: Dispatch<SetStateAction<FocusIntent | null>>;
    uploadTarget: Condition | null;
}

export const useConditionAttachDialog = ({
    showBanner,
    setFocusIntent,
    uploadTarget,
}: UseConditionAttachDialogOptions) => {
    const [attachTarget, setAttachTarget] = useState<Condition | null>(null);
    const { navigateToTab } = useTabNavigation();
    const pendingAttachedBannerRef = useRef<AttachedBanner | null>(null);

    const showAttachedBanner = ({ condition, count }: AttachedBanner) => {
        showBanner({
            kind: 'attached',
            fileCount: count,
            description: condition.description,
            onViewNow: () => {
                // A click on the tab header keeps every tab mounted; a ?tab= change would remount the Tabs.
                navigateToTab(CONDITIONS_TAB_TEST_ID);
                openAttachDialog(condition);
            },
        });
    };

    const handleFilesAttached = (condition: Condition, count: number) => {
        pendingAttachedBannerRef.current = { condition, count };
    };

    const takePendingAttachedBanner = () => {
        const pending = pendingAttachedBannerRef.current;
        pendingAttachedBannerRef.current = null;
        if (pending) showAttachedBanner(pending);
        return pending;
    };

    // The banner waits until the upload dialog has exited, so no View Now click or note-box focus races that exit.
    const handleUploadDialogExited = () => {
        const shown = takePendingAttachedBanner();
        if (shown) setFocusIntent({ conditionId: shown.condition.id, target: 'noteBox' });
    };

    // A dialog that re-opens during its exit never fires onExited, so the banner shows here.
    const prepareFileDialogOpen = () => {
        takePendingAttachedBanner();
        setFocusIntent((intent) => (intent?.target === 'noteBox' ? null : intent));
    };

    const openAttachDialog = (condition: Condition) => {
        prepareFileDialogOpen();
        setAttachTarget(condition);
    };
    const closeAttachDialog = () => setAttachTarget(null);

    const handleAllUploaded = (totalCount: number) => {
        if (!uploadTarget) return;
        handleFilesAttached(uploadTarget, totalCount);
    };

    return {
        attachTarget,
        openAttachDialog,
        closeAttachDialog,
        prepareFileDialogOpen,
        attachDialogProps: {
            onFilesAttached: handleFilesAttached,
            onUploadDialogExited: handleUploadDialogExited,
        },
        uploadDialogProps: { onAllUploaded: handleAllUploaded, onExited: handleUploadDialogExited },
    };
};
